import React, { useState, useEffect, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  TextField,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Divider,
  ListSubheader,
} from '@mui/material';
import {
  Search as SearchIcon,
  Speed as SpeedIcon,
  Clear as ClearIcon,
  Wifi as WifiIcon,
  AttachMoney as MoneyIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';

import { Planos } from '../../types';
import { planosService } from '../../services/planosService';
import { formatarValor } from '../../utils/formatToView';

interface PlanosSelectProps {
  value?: number | number[] | null;
  onChange: (value: number | number[] | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  onlyActive?: boolean;
  allowClear?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  escritorio_id?: number; // Filtrar planos por escritório específico
}

const PlanosSelect: React.FC<PlanosSelectProps> = ({
  value,
  onChange,
  label = 'Plano',
  size = 'medium',
  error = false,
  helperText,
  required = false,
  disabled = false,
  multiple = false,
  placeholder = 'Selecione um plano',
  onlyActive = true,
  allowClear = false,
  fullWidth = true,
  variant = 'outlined',
  escritorio_id,
}) => {
  const [allPlanos, setAllPlanos] = useState<Planos[]>([]);
  const [filteredPlanos, setFilteredPlanos] = useState<Planos[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [planoSelecionadoCache, setPlanoSelecionadoCache] = useState<Planos | null>(null);

  const selectedPlanoId = !multiple && typeof value === 'number' && value > 0 ? value : null;

  // Garantir que o plano já selecionado (edição) esteja disponível mesmo fora da busca/filtro
  useEffect(() => {
    if (!selectedPlanoId) {
      setPlanoSelecionadoCache(null);
      return;
    }

    const found = allPlanos.find((p) => p.id === selectedPlanoId);
    if (found) {
      setPlanoSelecionadoCache(found);
      return;
    }

    let cancelled = false;
    planosService
      .getById(selectedPlanoId)
      .then((plano) => {
        if (!cancelled) setPlanoSelecionadoCache(plano);
      })
      .catch(() => {
        if (!cancelled) setPlanoSelecionadoCache(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedPlanoId, allPlanos]);

  const planosExibicao = useMemo(() => {
    if (!planoSelecionadoCache || filteredPlanos.some((p) => p.id === planoSelecionadoCache.id)) {
      return filteredPlanos;
    }
    return [planoSelecionadoCache, ...filteredPlanos];
  }, [filteredPlanos, planoSelecionadoCache]);

  const filterPlanosByEscritorio = (planos: Planos[], escritorioId?: number): Planos[] => {
    if (!escritorioId) return planos;

    return planos.filter((plano) => {
      if (!plano.escritorio_id) return false;

      let escritorioIds: number[] = [];
      if (typeof plano.escritorio_id === 'string') {
        try {
          escritorioIds = JSON.parse(plano.escritorio_id);
        } catch {
          return false;
        }
      } else if (Array.isArray(plano.escritorio_id)) {
        escritorioIds = plano.escritorio_id;
      } else {
        escritorioIds = [plano.escritorio_id];
      }

      return escritorioIds.includes(escritorioId);
    });
  };

  // Carregar todos os planos do backend
  const loadAllPlanos = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const filters: any = {
        ativo: onlyActive ? 'true' as const : 'todos' as const,
        limit: 1000, // Carregar todos os planos
        search: searchTerm || undefined,
      };

      const response = await planosService.getForSelect(filters);
      
      if (response.success) {
        const planos = response.data || [];
        setAllPlanos(planos);
        
        const filtered = filterPlanosByEscritorio(planos, escritorio_id);
        setFilteredPlanos(filtered);
      } else {
        throw new Error(response.message || 'Erro ao carregar planos');
      }
    } catch (err: any) {
      console.error('Erro ao carregar planos:', err);
      setLoadError(err.message || 'Erro ao carregar planos');
      setAllPlanos([]);
      setFilteredPlanos([]);
    } finally {
      setLoading(false);
    }
  };

  // Carregar planos quando o componente montar ou quando mudar os filtros
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAllPlanos();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [onlyActive, searchTerm, escritorio_id]);

  // Filtrar planos quando mudar o escritório
  useEffect(() => {
    if (allPlanos.length > 0) {
      const filtered = filterPlanosByEscritorio(allPlanos, escritorio_id);
      setFilteredPlanos(filtered);
    }
  }, [escritorio_id, allPlanos]);

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;

    if (multiple) {
      if (Array.isArray(selectedValue)) {
        onChange(selectedValue.length > 0 ? selectedValue : null);
      } else {
        onChange([selectedValue]);
      }
    } else {
      const parsed = selectedValue === '' ? null : Number(selectedValue);
      onChange(parsed !== null && Number.isNaN(parsed) ? null : parsed);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const handleRemoveChip = (planoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(id => id !== planoId);
      onChange(newValue.length > 0 ? newValue : null);
    }
  };

  const getSelectedPlanos = (): Planos[] => {
    if (!value) return [];
    
    if (multiple && Array.isArray(value)) {
      return planosExibicao.filter((plano) => value.includes(plano.id));
    } else if (!multiple && typeof value === 'number') {
      const plano =
        planosExibicao.find((p) => p.id === value) ||
        (planoSelecionadoCache?.id === value ? planoSelecionadoCache : undefined);
      return plano ? [plano] : [];
    }
    
    return [];
  };

  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      return <Box component="span" sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
    }

    const selectedPlanos = getSelectedPlanos();
    
    if (multiple) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedPlanos.map((plano) => (
            <Chip
              key={plano.id}
              label={`${plano.nome} - ${plano.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}
              size="small"
              onDelete={(event) => handleRemoveChip(plano.id, event)}
              deleteIcon={<ClearIcon />}
              sx={{ 
                height: 24, 
                fontSize: '0.75rem',
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px'
                }
              }}
            />
          ))}
        </Box>
      );
    } else {
      const plano = selectedPlanos[0];
      return plano ? `${plano.nome} - ${plano.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}` : '';
    }
  };

  const formatPlanoLabel = (plano: Planos) => {
    let label = `${plano.nome}`;
    
    if (!plano.ativo) {
      label += ' (Inativo)';
    }
    
    return label;
  };

  const getVelocidadeIcon = () => {
    return <SpeedIcon fontSize="small" />;
  };

  const getVelocidadeColor = (velocidade: number) => {
    if (velocidade >= 100) return 'success';
    if (velocidade >= 50) return 'primary';
    if (velocidade >= 25) return 'warning';
    return 'default';
  };

  const formatVelocidade = (velocidade: number) => {
    if (velocidade >= 1000) {
      return `${(velocidade / 1000).toFixed(1)} Gbps`;
    }
    return `${velocidade} Mbps`;
  };

  const planosPorGrupo = useMemo(() => {
    const gruposMap = new Map<number, { titulo: string; planos: Planos[] }>();

    planosExibicao.forEach((plano) => {
      const grupoId = plano.grupo_id ?? plano.grupo?.id ?? 0;
      const titulo = plano.grupo?.titulo || 'Sem grupo';

      if (!gruposMap.has(grupoId)) {
        gruposMap.set(grupoId, { titulo, planos: [] });
      }
      gruposMap.get(grupoId)!.planos.push(plano);
    });

    return Array.from(gruposMap.entries())
      .map(([grupoId, data]) => ({
        grupoId,
        titulo: data.titulo,
        planos: data.planos.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
      }))
      .sort((a, b) => a.titulo.localeCompare(b.titulo, 'pt-BR'));
  }, [planosExibicao]);

  const valorNoMenu = useMemo(() => {
    if (multiple) return true;
    if (selectedPlanoId == null) return true;
    return planosExibicao.some((p) => p.id === selectedPlanoId);
  }, [multiple, selectedPlanoId, planosExibicao]);

  const selectValue = multiple
    ? (Array.isArray(value) ? value : [])
    : valorNoMenu
      ? (value ?? '')
      : '';

  const renderPlanoMenuItem = (plano: Planos) => (
    <MenuItem key={plano.id} value={plano.id} sx={{ pl: 2.5 }}>
      <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          {multiple && (
            <Checkbox
              checked={Array.isArray(value) ? value.includes(plano.id) : false}
              size="small"
            />
          )}

          {getVelocidadeIcon()}

          <Box flex={1}>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="bold">{plano.nome}</Typography>
                  {!plano.ativo && (
                    <Chip
                      label="Inativo"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Box
                    component="div"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    {formatVelocidade(plano.velocidade_download)} ↓ / {formatVelocidade(plano.velocidade_upload)} ↑
                  </Box>
                  {plano.descricao && (
                    <Box
                      component="div"
                      sx={{
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      {plano.descricao}
                    </Box>
                  )}
                </Box>
              }
              primaryTypographyProps={{
                component: 'div',
                fontSize: '0.875rem',
                fontWeight: 'normal',
              }}
              secondaryTypographyProps={{
                component: 'div',
                fontSize: '0.75rem',
                color: plano.ativo ? 'text.secondary' : 'error.main',
              }}
            />
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} sx={{ ml: 2 }}>
          <Chip
            label={formatarValor(plano.valor)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              height: 28,
              fontSize: '0.8rem',
              fontWeight: 'bold',
              backgroundColor: 'primary.50',
              borderColor: 'primary.main',
              '& .MuiChip-label': {
                px: 1.5,
              },
            }}
          />
        </Box>
      </Box>
    </MenuItem>
  );

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      error={error} 
      variant={variant}
      disabled={disabled}
    >
      <InputLabel required={required}>
        {label}
      </InputLabel>
      
      <Select
        value={selectValue}
        label={label}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
        }}
        multiple={multiple}
        renderValue={renderValue}
        MenuProps={{
          disableScrollLock: true, // Previne recálculo da barra de rolagem no body
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
          TransitionProps: {
            onExited: () => {
              setSearchTerm('');
            }
          }
        }}
        startAdornment={
          <InputAdornment position="start">
            <WifiIcon />
          </InputAdornment>
        }
        endAdornment={
          allowClear && value && (
            <InputAdornment position="end">
              <ClearIcon 
                onClick={handleClear}
                sx={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  '&:hover': { color: 'error.main' }
                }}
              />
            </InputAdornment>
          )
        }
      >
        {/* Campo de busca */}
          <Box sx={{ 
            p: 1, 
            borderBottom: 1, 
            borderColor: 'divider' 
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por nome do plano..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ p: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'grey.50',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'grey.100'
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.09)' 
                      : 'background.paper'
                  }
                }
              }}
            />
            {searchTerm && (
              <Box sx={{ mt: 1, px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Buscando planos com nome: "{searchTerm}"
                </Typography>
              </Box>
            )}
            {escritorio_id && (
              <Box sx={{ mt: 1, px: 1 }}>
                <Typography variant="caption" color="primary.main">
                  Filtrando planos do escritório selecionado
                </Typography>
              </Box>
            )}
          </Box>

        {/* Loading */}
        {loading && planosExibicao.length === 0 && (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <span>Carregando planos...</span>
            </Box>
          </MenuItem>
        )}

        {/* Erro */}
        {loadError && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%', py: 0.5 }}>
              {loadError}
            </Alert>
          </MenuItem>
        )}

        {/* Opção vazia (apenas para seleção única) */}
        {!multiple && !required && !loadError && (
          <MenuItem value="">
            <em>Nenhum plano</em>
          </MenuItem>
        )}

        {/* Plano selecionado ainda não carregado no menu */}
        {!multiple && selectedPlanoId && !valorNoMenu && (
          <MenuItem value={selectedPlanoId}>
            {planoSelecionadoCache
              ? formatPlanoLabel(planoSelecionadoCache)
              : loading
                ? 'Carregando plano selecionado...'
                : `Plano #${selectedPlanoId}`}
          </MenuItem>
        )}

        {/* Lista de planos agrupados por grupo — filhos diretos do Select (sem Box) */}
        {!loadError &&
          planosPorGrupo.flatMap((grupo) => [
            <ListSubheader
              key={`grupo-${grupo.grupoId}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                lineHeight: '36px',
                bgcolor: 'action.hover',
              }}
            >
              <FolderIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={700} component="span">
                {grupo.titulo}
              </Typography>
              <Chip
                size="small"
                label={grupo.planos.length}
                variant="outlined"
                color="primary"
                sx={{ height: 20, fontSize: '0.65rem', ml: 'auto' }}
              />
            </ListSubheader>,
            ...grupo.planos.map((plano) => renderPlanoMenuItem(plano)),
          ])}

        {/* Nenhum resultado */}
        {!loading && !loadError && planosExibicao.length === 0 && (
          <MenuItem disabled>
            <em>
              {escritorio_id 
                ? 'Nenhum plano disponível para este escritório' 
                : 'Nenhum plano encontrado'
              }
            </em>
          </MenuItem>
        )}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default PlanosSelect;
